import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { translations } from "./translations";

const STORAGE_KEY = "alpe_lang";
const SUPPORTED = ["en", "fr"];

const LanguageContext = createContext(null);

function detectInitialLang() {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
    const nav = (window.navigator?.language || "").toLowerCase();
    if (nav.startsWith("fr")) return "fr";
    return "en";
}

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(detectInitialLang);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, lang);
        } catch (_) {
            // ignore storage failures
        }
        if (typeof document !== "undefined") {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    const setLang = useCallback((next) => {
        if (SUPPORTED.includes(next)) setLangState(next);
    }, []);

    const toggle = useCallback(() => {
        setLangState((cur) => (cur === "en" ? "fr" : "en"));
    }, []);

    const value = useMemo(
        () => ({
            lang,
            setLang,
            toggle,
            t: translations[lang],
        }),
        [lang, setLang, toggle],
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useT() {
    const ctx = useContext(LanguageContext);
    if (!ctx)
        throw new Error("useT must be used within a LanguageProvider");
    return ctx;
}
